import { registerDismissable } from "@scribenote/brian-design-system";
import { useEffect, useRef } from "react";

/**
 * Dismiss a popup on outside-click or Escape, for a plain positioned popup that
 * is not a kit overlay (kit overlays get this from `useDialog`/`usePopover`).
 * No-op while `open` is false. Returns a ref to attach to the popup's container
 * element — clicks inside it (or anywhere while closed) don't dismiss.
 *
 * Joins the kit's dismissable registry while open, so the Escape that closes the
 * popup can't also count toward Companion's Esc-Esc (see useCompanionShortcuts).
 */
export const useDismissable = <T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onDismiss: () => void,
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!open) return;
    const release = registerDismissable();
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
      release();
    };
  }, [open, onDismiss]);

  return ref;
};
